/**
 * Utility functions to convert ProductDetail to API update payload format
 * Handles the differences between GET and PUT API structures
 */

import { ProductDetail } from '../types/ProductTypes';

/**
 * Product Update Response Interface
 * Response from the PUT API endpoint
 */
export interface ProductUpdateResponse {
    success: boolean;
    message?: string;
    data?: any;
    errors?: string[];
}

/**
 * Convert ProductDetail to API update payload format
 * Handles structural differences between GET response and PUT request
 */
export function convertProductDetailToUpdatePayload(product: ProductDetail, updates?: Partial<ProductDetail>): any {
    // Merge any updates with the existing product
    const merged = { ...product, ...updates };

    return {
        LastUpdatedUtc: new Date().toISOString(),
        Id: merged.id,
        CategoryId: merged.categoryId || merged.category?.id || product?.categoryId || product?.category?.id || 0,
        TradeName: merged.tradeName || '',
        MainHazards: merged.mainHazards || '',
        IsNonCargo: merged.isNonCargo,
        IsActive: merged.isActive,
        IsApproved: merged.isApproved,

        // Convert Synonyms: handle both string[] and object[] formats
        Synonyms:
            merged.synonyms?.map((syn: any) => {
                // If syn is already an object with synonymName, use it
                if (typeof syn === 'object' && syn !== null && 'synonymName' in syn) {
                    return {
                        Id: syn.id || 0,
                        SynonymName: syn.synonymName,
                    };
                }
                // If syn is a string, convert it
                return {
                    Id: 0, // Backend will handle ID assignment
                    SynonymName: String(syn),
                };
            }) || [],

        // Convert Procedures
        Procedures:
            merged.procedures?.map((proc: any) => ({
                Id: proc.id,
                LastUpdatedUtc: proc.lastUpdatedUtc,
                Title: proc.title,
                Description: proc.description,
                Comments: proc.comments || null,
                FilePath: proc.filePath || null,
                Category: proc.category,
                Score: proc.score,
            })) || [],

        // Convert Coating Compatibilities
        // Note: MaxTemperature must be sent as a Temperature object { Value: number } because
        // the C# Temperature class only has a parameterized constructor Temperature(decimal value)
        // and Newtonsoft.Json cannot deserialize a raw number into it.
        CoatingCompatibilities:
            merged.coatingCompatibilities?.map((cc: any) => {
                const coat = cc.coating || {};
                const mfr = coat.Manufacturer ?? coat.manufacturer ?? {};
                return {
                    Id: cc.id,
                    Coating: {
                        Id: coat.Id ?? coat.id,
                        Name: coat.Name ?? coat.name,
                        Type: coat.Type ?? coat.type,
                        Manufacturer: {
                            Id: mfr.Id ?? mfr.id ?? 0,
                            Name: mfr.Name ?? mfr.name ?? '',
                            LastUpdatedUtc: mfr.LastUpdatedUtc ?? mfr.lastUpdatedUtc ?? new Date().toISOString(),
                        },
                        IsActive: coat.IsActive ?? coat.isActive,
                        LastUpdatedUtc: coat.LastUpdatedUtc ?? coat.lastUpdatedUtc ?? new Date().toISOString(),
                        ExternalId: coat.ExternalId ?? coat.externalId ?? '',
                    },
                    Compatibility: cc.compatibility,
                    MaxTemperature: cc.maxTemperature != null ? { Value: cc.maxTemperature } : null,
                    CoatingNotes: cc.coatingNotes || [],
                    LastUpdatedUtc: cc.lastUpdatedUtc ?? new Date().toISOString(),
                };
            }) || [],

        // External IDs
        ExternalIds:
            merged.externalIds?.map((extId: any) => ({
                Id: extId.id,
                Value: extId.value,
                Source: extId.source,
                LastUpdatedUtc: extId.lastUpdatedUtc,
            })) || [],

        // Comments
        Comments:
            merged.comments?.map((comment: any) => ({
                Id: comment.id,
                Text: comment.text,
                Category: comment.category,
                Visibility: comment.visibility,
                LastUpdatedUtc: comment.lastUpdatedUtc,
            })) || [],

        // Stowage Rules
        StowageRules: merged.stowageRules || [],

        // Temperature Requirements
        TemperatureRequirements: merged.temperatureRequirements,

        // Physical Data - convert TemperatureValue objects to strings if needed
        PhysicalData: merged.physicalData ? convertPhysicalDataForUpdate(merged.physicalData) : null,

        // Gas Physical Data
        GasPhysicalData: merged.gasPhysicalData || null,

        // Category - simplified structure for update
        Category: merged.category
            ? {
                Id: merged.category.id,
                Name: merged.category.name,
                IsStaticAccumulator: merged.category.isStaticAccumulator,
                ThresholdLimitValueTimeWeightedAverage: merged.category.thresholdLimitValueTimeWeightedAverage,
                OdorThreshold: merged.category.odorThreshold,
                ExternalIds: merged.category.externalIds || [],
                Synonyms: merged.category.synonyms || [],
                Comments: merged.category.comments || [],
                DraegerTubes: merged.category.draegerTubes || [],
                StowageRules: merged.category.stowageRules || [],
                Regulations: merged.category.regulations ? {
                    MarpolPollutionCategory: merged.category.regulations.marpolPollutionCategory,
                    MarpolRegulation: merged.category.regulations.marpolRegulation,
                    IbcRegulation: merged.category.regulations.ibcRegulation ? {
                        Id: merged.category.regulations.ibcRegulation.id,
                        Hazards: merged.category.regulations.ibcRegulation.hazards,
                        ShipType: merged.category.regulations.ibcRegulation.shipType,
                        TankType: merged.category.regulations.ibcRegulation.tankType,
                        TankVents: merged.category.regulations.ibcRegulation.tankVents,
                        TankEnvironmentalControl: merged.category.regulations.ibcRegulation.tankEnvironmentalControl,
                        Gauging: merged.category.regulations.ibcRegulation.gauging,
                        VapourDetection: merged.category.regulations.ibcRegulation.vapourDetection,
                        FireProtection: merged.category.regulations.ibcRegulation.fireProtection,
                        RequiresEmergencyEquipment: merged.category.regulations.ibcRegulation.requiresEmergencyEquipment,
                        SpecificAndOperationalRequirements: merged.category.regulations.ibcRegulation.specificAndOperationalRequirements?.map((req: any) => ({
                            Id: req.id,
                            ParagraphNumber: req.paragraphNumber,
                            ParagraphHeading: req.paragraphHeading,
                            Text: req.text,
                            LastUpdatedUtc: req.lastUpdatedUtc,
                        })) || [],
                        LastUpdatedUtc: merged.category.regulations.ibcRegulation.lastUpdatedUtc,
                    } : null,
                    IgcRegulation: merged.category.regulations.igcRegulation ? {
                        Id: merged.category.regulations.igcRegulation.id,
                        SpecificAndOperationalRequirements: merged.category.regulations.igcRegulation.specificAndOperationalRequirements?.map((req: any) => ({
                            Id: req.id,
                            ParagraphNumber: req.paragraphNumber,
                            ParagraphHeading: req.paragraphHeading,
                            Text: req.text,
                            LastUpdatedUtc: req.lastUpdatedUtc,
                        })) || [],
                        LastUpdatedUtc: merged.category.regulations.igcRegulation.lastUpdatedUtc,
                    } : null,
                } : undefined,
                PhysicalData: merged.category.physicalData || {},
                SafetyEquipment: merged.category.safetyEquipment ? {
                    Id: merged.category.safetyEquipment.id,
                    NormalExposureLevel: merged.category.safetyEquipment.normalExposureLevel,
                    HighExposureLevel: merged.category.safetyEquipment.highExposureLevel,
                    ExtremeExposureLevel: merged.category.safetyEquipment.extremeExposureLevel,
                    SafetyHazards: merged.category.safetyEquipment.safetyHazards?.map((h: any) => ({
                        Id: h.id,
                        HazardCode: h.hazardCode,
                        HazardDescription: h.hazardDescription,
                        NormalExposureLevel: h.normalExposureLevel,
                        HighExposureLevel: h.highExposureLevel,
                        ExtremeExposureLevel: h.extremeExposureLevel,
                        WarningLevel: h.warningLevel,
                        WarningImage: h.warningImage,
                        Category: h.category,
                        HealthPropertiesScore: h.healthPropertiesScore,
                        PhysicalPropertiesScore: h.physicalPropertiesScore,
                        EnvironmentPropertiesScore: h.environmentPropertiesScore,
                        RequiresExposureLog: h.requiresExposureLog,
                        IsActive: h.isActive,
                        LastUpdatedUtc: h.lastUpdatedUtc,
                    })) || [],
                    LastUpdatedUtc: merged.category.safetyEquipment.lastUpdatedUtc,
                } : undefined,
                LastUpdatedUtc: merged.category.lastUpdatedUtc,
                IsInhibited: merged.category.isInhibited,
                ShallNotBeExposedToExcessiveHeat: merged.category.shallNotBeExposedToExcessiveHeat,
            }
            : undefined,

        // Charterer - backend expects { Id, ExternalId, Name, Code }
        Charterer: merged.charterer
            ? {
                Id: merged.charterer.id,
                ExternalId: merged.charterer.externalId,
                Name: merged.charterer.name,
                Code: merged.charterer.code,
            }
            : null,

        // Regulations - simplified for update
        Regulations: merged.regulations
            ? {
                TripartiteAgreement: merged.regulations.tripartiteAgreement
                    ? {
                        Id: merged.regulations.tripartiteAgreement.id ?? 0,
                        LastUpdatedUtc: merged.regulations.tripartiteAgreement.lastUpdatedUtc ?? new Date().toISOString(),
                        ReportingCountry: merged.regulations.tripartiteAgreement.reportingCountry ?? null,
                        ExpiryDate: merged.regulations.tripartiteAgreement.expiryDate ?? null,
                        Countries: merged.regulations.tripartiteAgreement.countries ?? null,
                        Contains: merged.regulations.tripartiteAgreement.contains ?? null,
                    }
                    : null,
                USCGGroup: merged.regulations.uscgGroup ? { Id: merged.regulations.uscgGroup.id } : null,
                IMDGCodeClass: merged.regulations.imdgCodeClass ? { Id: merged.regulations.imdgCodeClass.id } : null,
                PersistentFloater: merged.regulations.persistentFloater,
                NumberOfLastCargoesForEpoxyTanksFosfaBan: merged.regulations.numberOfLastCargoesForEpoxyTanksFosfaBan,
                FosfaAllowance: merged.regulations.fosfaAllowance,
                EuAllowance: merged.regulations.euAllowance,
                Niop1Allowance: merged.regulations.niop1Allowance,
                Niop2Allowance: merged.regulations.niop2Allowance,
            }
            : undefined,

        // Material Safety Data Sheets
        MaterialSafetyDataSheet: merged.materialSafetyDataSheet
            ? {
                Id: merged.materialSafetyDataSheet.id,
                FilePath: merged.materialSafetyDataSheet.filePath,
                LastUpdatedUtc: merged.materialSafetyDataSheet.lastUpdatedUtc,
                Comments: merged.materialSafetyDataSheet.comments,
                ReceivedDate: merged.materialSafetyDataSheet.receivedDate,
                ReceivedFrom: merged.materialSafetyDataSheet.receivedFrom,
                Date: merged.materialSafetyDataSheet.date,
                Version: merged.materialSafetyDataSheet.version,
            }
            : null,
        MaterialSafetyDataSheets: (merged.materialSafetyDataSheets || []).map((msds: any) => ({
            Id: msds.id ?? msds.Id ?? 0,
            FilePath: msds.filePath ?? msds.FilePath ?? '',
            LastUpdatedUtc: msds.lastUpdatedUtc ?? msds.LastUpdatedUtc ?? new Date().toISOString(),
            Comments: msds.comments ?? msds.Comments ?? null,
            ReceivedDate: msds.receivedDate ?? msds.ReceivedDate ?? null,
            ReceivedFrom: msds.receivedFrom ?? msds.ReceivedFrom ?? null,
            Date: msds.date ?? msds.Date ?? null,
            Version: msds.version ?? msds.Version ?? null,
        })),

        // Product Documents
        ProductDocuments: (merged.productDocuments || []).map((doc: any) => ({
            Id: doc.id ?? doc.Id ?? 0,
            FilePath: doc.filePath ?? doc.FilePath ?? '',
            LastUpdatedUtc: doc.lastUpdatedUtc ?? doc.LastUpdatedUtc ?? new Date().toISOString(),
        })),

        // Draeger Tubes
        DraegerTubes: merged.draegerTubes || [],
    };
}

/**
 * Convert PhysicalData for update API
 * Backend expects PascalCase property names with Weight and Temperature objects
 * Frontend uses camelCase, so we need to convert property names
 */
function convertPhysicalDataForUpdate(physicalData: any): any {
    if (!physicalData) return null;

    // Helper to convert TemperatureValue from camelCase to PascalCase
    const convertTempValue = (val: any) => {
        if (!val) return null;
        return { Value: val.value ?? val.Value ?? null };
    };

    // Helper to convert TemperatureRange from camelCase to PascalCase
    const convertTempRange = (range: any) => {
        if (!range) return null;
        return {
            MinValue: convertTempValue(range.minValue ?? range.MinValue),
            MaxValue: convertTempValue(range.maxValue ?? range.MaxValue),
        };
    };

    // Helper to convert ValueRange (numeric min/max) from camelCase to PascalCase
    const convertValueRange = (range: any) => {
        if (!range) return null;
        return {
            MinValue: range.minValue ?? range.MinValue ?? null,
            MaxValue: range.maxValue ?? range.MaxValue ?? null,
        };
    };

    return {
        // Weight type - expects { Value: decimal }
        LitreWeight: convertTempValue(physicalData.litreWeight ?? physicalData.LitreWeight),
        LitreWeightReferenceTemperature: convertTempValue(physicalData.litreWeightReferenceTemperature ?? physicalData.LitreWeightReferenceTemperature),
        LitreWeightCorrectionFactor: physicalData.litreWeightCorrectionFactor ?? physicalData.LitreWeightCorrectionFactor ?? null,

        // Temperature ranges
        MeltingPointRange: convertTempRange(physicalData.meltingPointRange ?? physicalData.MeltingPointRange),
        BoilingPointRange: convertTempRange(physicalData.boilingPointRange ?? physicalData.BoilingPointRange),
        FlashPointRange: convertTempRange(physicalData.flashPointRange ?? physicalData.FlashPointRange),

        // Value ranges (numeric)
        ViscosityRange: convertValueRange(physicalData.viscosityRange ?? physicalData.ViscosityRange),
        VapourPressureRange: convertValueRange(physicalData.vapourPressureRange ?? physicalData.VapourPressureRange),
        PHRange: convertValueRange(physicalData.phRange ?? physicalData.PHRange),

        // Reference temperatures
        ViscosityReferenceTemperature: convertTempValue(physicalData.viscosityReferenceTemperature ?? physicalData.ViscosityReferenceTemperature),
        TemperatureToAchieve50mPas: convertTempValue(physicalData.temperatureToAchieve50mPas ?? physicalData.TemperatureToAchieve50mPas),
        VapourPressureReferenceTemperature: convertTempValue(physicalData.vapourPressureReferenceTemperature ?? physicalData.VapourPressureReferenceTemperature),

        // Enums and simple values
        SolubleInWater: physicalData.solubleInWater ?? physicalData.SolubleInWater ?? null,
        IsStaticAccumulator: physicalData.isStaticAccumulator ?? physicalData.IsStaticAccumulator ?? null,
        ThresholdLimitValueTimeWeightedAverage: physicalData.thresholdLimitValueTimeWeightedAverage ?? physicalData.ThresholdLimitValueTimeWeightedAverage ?? null,
        OdorThreshold: physicalData.odorThreshold ?? physicalData.OdorThreshold ?? null,
        VapourDensity: physicalData.vapourDensity ?? physicalData.VapourDensity ?? null,
        Origin: physicalData.origin ?? physicalData.Origin ?? null,
    };
}

/**
 * Helper function to update specific fields in a ProductDetail
 * Returns a new object ready for API submission
 */
export function updateProductFields(product: ProductDetail, fieldUpdates: Partial<ProductDetail>): any {
    return convertProductDetailToUpdatePayload(product, fieldUpdates);
}

/**
 * Helper to build update payload with changed fields merged into full product
 * API requires the complete product object, not just changed fields
 */
export function buildMinimalUpdatePayload(
    product: ProductDetail,
    changedFields: {
        tradeName?: string;
        synonyms?: string[];
        casNumber?: string;
        description?: string;
        warning?: string;
        charterer?: { id: number; name: string; code: string; externalId: number; lastUpdatedUtc: string };
        categoryName?: string;
        materialSafetyDataSheets?: { id: number; filePath: string; lastUpdatedUtc: string; comments: string | null; receivedDate: string | null; receivedFrom: string | null; date: string | null; version: string | null }[];
        productDocuments?: { id: number; filePath: string; lastUpdatedUtc: string }[];
        additionalComments?: { id: number; text: string; category: number; visibility: number; lastUpdatedUtc: string }[];
        coatingCompatibilities?: { id: number; coating: any; compatibility: number; maxTemperature: number | null; coatingNotes: any[]; lastUpdatedUtc: string }[];
        procedures?: { id: number; lastUpdatedUtc: string; title: string; description: string; comments: string | null; filePath: string | null; category: number; score: number }[];
        externalIds?: { id: number; value: string; source: string; lastUpdatedUtc: string }[];
        stowageRules?: { id: number; name: string; lastUpdatedUtc?: string }[];
        temperatureRequirements?: any;
        regulations?: any;
        category?: any;
        physicalData?: any;
        gasPhysicalData?: any;
        isActive?: boolean;
    }
): any {
    // Start with the full product, converted to API format
    // Using 'any' because we're building an API payload with PascalCase properties
    const updates: any = {};

    // Apply changed fields to the updates object (using camelCase to match ProductDetail format)
    if (changedFields.tradeName !== undefined) {
        updates.tradeName = changedFields.tradeName;
    }

    if (changedFields.isActive !== undefined) {
        updates.isActive = changedFields.isActive;
    }

    if (changedFields.synonyms !== undefined) {
        updates.synonyms = changedFields.synonyms;
    }

    // Handle CAS Number (stored in ExternalIds array)
    if (changedFields.casNumber !== undefined) {
        const casExtId = product.externalIds?.find((extId) => extId.source === 'CAS');
        const otherExtIds = product.externalIds?.filter((extId) => extId.source !== 'CAS') || [];

        // Only add CAS external ID if it has a value
        if (changedFields.casNumber && changedFields.casNumber.trim() !== '') {
            updates.externalIds = [
                ...otherExtIds,
                {
                    id: casExtId?.id || 0,
                    value: changedFields.casNumber,
                    source: 'CAS',
                    lastUpdatedUtc: new Date().toISOString(),
                },
            ];
        } else {
            // If CAS number is empty, just keep other external IDs
            updates.externalIds = otherExtIds;
        }
    }

    // Handle Description, Warning, and Additional Comments (stored in Comments array)
    if (changedFields.description !== undefined || changedFields.warning !== undefined || changedFields.additionalComments !== undefined) {
        const descComment = product.comments?.find((c) => c.category === 4);
        const warnComment = product.comments?.find((c) => c.category === 5);

        // Get categories that will be replaced by additionalComments
        const additionalCategories = new Set(changedFields.additionalComments?.map((c) => c.category) || []);

        // Get other comments (excluding description, warning, and those being replaced by additionalComments)
        const otherComments = product.comments?.filter((c) =>
            c.category !== 4 && c.category !== 5 && !additionalCategories.has(c.category)
        ) || [];

        // Collect updated comments (using camelCase to match ProductDetail format)
        const updatedComments: any[] = otherComments.map((c) => ({
            id: c.id,
            text: c.text,
            category: c.category,
            visibility: c.visibility,
            lastUpdatedUtc: c.lastUpdatedUtc,
        }));

        // Add/update description comment (Category 4)
        if (changedFields.description !== undefined) {
            updatedComments.push({
                id: descComment?.id || 0,
                text: changedFields.description,
                category: 4,
                visibility: 1,
                lastUpdatedUtc: new Date().toISOString(),
            });
        } else if (descComment) {
            updatedComments.push({
                id: descComment.id,
                text: descComment.text,
                category: descComment.category,
                visibility: descComment.visibility,
                lastUpdatedUtc: descComment.lastUpdatedUtc,
            });
        }

        // Add/update warning comment (Category 5)
        if (changedFields.warning !== undefined && changedFields.warning) {
            updatedComments.push({
                id: warnComment?.id || 0,
                text: changedFields.warning,
                category: 5,
                visibility: 1,
                lastUpdatedUtc: new Date().toISOString(),
            });
        } else if (warnComment) {
            updatedComments.push({
                id: warnComment.id,
                text: warnComment.text,
                category: warnComment.category,
                visibility: warnComment.visibility,
                lastUpdatedUtc: warnComment.lastUpdatedUtc,
            });
        }

        // Add additional comments (e.g., coating comments from other sections)
        if (changedFields.additionalComments) {
            changedFields.additionalComments.forEach((c) => {
                updatedComments.push({
                    id: c.id,
                    text: c.text,
                    category: c.category,
                    visibility: c.visibility,
                    lastUpdatedUtc: c.lastUpdatedUtc,
                });
            });
        }

        updates.comments = updatedComments;
    }

    // Handle Charterer change - set the full charterer object from dropdown selection
    if (changedFields.charterer !== undefined) {
        updates.charterer = changedFields.charterer;
    }

    // Handle Category name change - use camelCase to match ProductDetail type
    if (changedFields.categoryName !== undefined && product.category) {
        updates.category = {
            ...product.category,
            name: changedFields.categoryName,
        };
    }

    // Handle MaterialSafetyDataSheets update
    if (changedFields.materialSafetyDataSheets !== undefined) {
        updates.materialSafetyDataSheets = changedFields.materialSafetyDataSheets.map((msds) => ({
            id: msds.id,
            filePath: msds.filePath,
            lastUpdatedUtc: msds.lastUpdatedUtc,
            comments: msds.comments,
            receivedDate: msds.receivedDate,
            receivedFrom: msds.receivedFrom,
            date: msds.date,
            version: msds.version,
        }));
    }

    // Handle ProductDocuments update
    if (changedFields.productDocuments !== undefined) {
        updates.productDocuments = changedFields.productDocuments.map((doc) => ({
            id: doc.id,
            filePath: doc.filePath,
            lastUpdatedUtc: doc.lastUpdatedUtc,
        }));
    }

    // Handle CoatingCompatibilities update
    // Ensure maxTemperature is stored as a plain number in the intermediate format;
    // convertProductDetailToUpdatePayload will wrap it into { Value: number } for the API
    if (changedFields.coatingCompatibilities !== undefined) {
        updates.coatingCompatibilities = changedFields.coatingCompatibilities.map((cc) => ({
            id: cc.id,
            coating: cc.coating,
            compatibility: cc.compatibility,
            maxTemperature: cc.maxTemperature != null
                ? (typeof cc.maxTemperature === 'object' ? cc.maxTemperature.value ?? cc.maxTemperature.Value : cc.maxTemperature)
                : null,
            coatingNotes: cc.coatingNotes || [],
            lastUpdatedUtc: cc.lastUpdatedUtc,
        }));
    }

    // Handle Procedures update
    if (changedFields.procedures !== undefined) {
        updates.procedures = changedFields.procedures.map((proc) => ({
            id: proc.id,
            lastUpdatedUtc: proc.lastUpdatedUtc,
            title: proc.title,
            description: proc.description,
            comments: proc.comments,
            filePath: proc.filePath,
            category: proc.category,
            score: proc.score,
        }));
    }

    // Handle StowageRules update
    if (changedFields.stowageRules !== undefined) {
        updates.stowageRules = changedFields.stowageRules.map((rule) => ({
            id: rule.id,
            name: rule.name,
            lastUpdatedUtc: rule.lastUpdatedUtc || new Date().toISOString(),
        }));
    }

    // Handle ExternalIds update
    if (changedFields.externalIds !== undefined) {
        updates.externalIds = changedFields.externalIds.map((extId) => ({
            id: extId.id && extId.id > 0 ? extId.id : 0,
            value: extId.value,
            source: extId.source,
            lastUpdatedUtc: extId.lastUpdatedUtc || new Date().toISOString(),
        }));
    }

    // Handle TemperatureRequirements update
    if (changedFields.temperatureRequirements !== undefined) {
        updates.temperatureRequirements = changedFields.temperatureRequirements;
    }

    // Handle Regulations update (e.g., Fosfa value)
    if (changedFields.regulations !== undefined) {
        updates.regulations = {
            ...product.regulations,
            ...changedFields.regulations,
        };
    }

    // Handle PhysicalData update
    if (changedFields.physicalData !== undefined) {
        updates.physicalData = changedFields.physicalData;
    }

    // Handle GasPhysicalData update
    if (changedFields.gasPhysicalData !== undefined) {
        updates.gasPhysicalData = changedFields.gasPhysicalData;
    }

    // Handle Category update (e.g., IBC requirements, name change, safety equipment)
    // Use updates.category (may have been set by categoryName handler) as base,
    // falling back to product.category. This ensures the full category is preserved
    // even when changedFields.category is a partial object (e.g., {name: 'newName'}).
    if (changedFields.category !== undefined) {
        const baseCategory = updates.category || product.category || {};

        updates.category = {
            ...baseCategory,
            ...changedFields.category,
            // Deep merge regulations if both exist
            regulations: changedFields.category?.regulations ? {
                ...(baseCategory.regulations || {}),
                ...changedFields.category.regulations,
                // Deep merge ibcRegulation if it exists
                ibcRegulation: changedFields.category.regulations?.ibcRegulation ? {
                    ...(baseCategory.regulations?.ibcRegulation || {}),
                    ...changedFields.category.regulations.ibcRegulation,
                } : baseCategory.regulations?.ibcRegulation,
            } : baseCategory.regulations,
            // Deep merge safetyEquipment if changed
            safetyEquipment: changedFields.category?.safetyEquipment ? {
                ...(baseCategory.safetyEquipment || {}),
                ...changedFields.category.safetyEquipment,
            } : baseCategory.safetyEquipment,
        };
    }

    // Use the full converter with the updates merged in
    return convertProductDetailToUpdatePayload(product, updates);
}

/**
 * Validation result interface
 */
export interface ValidationResult {
    isValid: boolean;
    errors: string[];
    warnings: string[];
}

/**
 * Validates product data before sending to UpsertProduct API
 * Mirrors the backend ProductValidation.cs validation rules
 */
export function validateProductForUpsert(payload: any): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!payload) {
        errors.push('Payload is required.');
        return { isValid: false, errors, warnings };
    }

    // Basic required fields
    if (!payload.TradeName && !payload.Category?.Name) {
        errors.push('Either TradeName or Category.Name must be provided.');
    }

    if (!payload.CategoryId && !payload.Category) {
        errors.push('CategoryId or embedded Category is required.');
    }

    // Validation for approved products (mirrors backend ProductValidation.cs)
    if (payload.IsApproved) {
        // USCG Group required
        if (!payload.Regulations?.USCGGroup?.Id) {
            errors.push('USCG Group is required for approved products.');
        }

        // PhysicalData validation
        const pd = payload.PhysicalData;
        if (!pd?.LitreWeight?.Value && pd?.LitreWeight?.Value !== 0) {
            errors.push('LitreWeight is required for approved products.');
        }
        if (pd?.LitreWeightCorrectionFactor === null || pd?.LitreWeightCorrectionFactor === undefined) {
            errors.push('LitreWeightCorrectionFactor is required for approved products.');
        }
        if (!pd?.LitreWeightReferenceTemperature?.Value && pd?.LitreWeightReferenceTemperature?.Value !== 0) {
            errors.push('LitreWeightReferenceTemperature is required for approved products.');
        }

        // TemperatureRequirements validation
        const tr = payload.TemperatureRequirements;
        if (!tr?.maxAdjacentTemperature?.value && tr?.maxAdjacentTemperature?.value !== 0) {
            errors.push('MaxAdjacentTemperature is required for approved products.');
        }
        if (!tr?.voyageTemperatureRange?.maxValue?.value && tr?.voyageTemperatureRange?.maxValue?.value !== 0) {
            errors.push('VoyageTemperatureRange.MaxValue is required for approved products.');
        }

        // Charterer required
        if (!payload.Charterer?.Id || payload.Charterer.Id <= 0) {
            errors.push('Charterer is required for approved products.');
        }
    }

    // Regulation-based validation for product categories
    const marpolReg = payload.Category?.Regulations?.MarpolRegulation;
    if (marpolReg !== null && marpolReg !== undefined) {
        // All Annex types require Product Name (Category.Name)
        if (marpolReg === 1 || marpolReg === 2 || marpolReg === 3) {
            if (!payload.Category?.Name || payload.Category.Name.trim() === '') {
                errors.push('Product Name is required for the selected regulation.');
            }
        }

        // Annex II - Chapter 17 (2): Additional required fields
        if (marpolReg === 2) {
            if (payload.Category?.Regulations?.MarpolPollutionCategory === null ||
                payload.Category?.Regulations?.MarpolPollutionCategory === undefined ||
                payload.Category?.Regulations?.MarpolPollutionCategory === 0) {
                errors.push('Marpol Pollution Category is required for Annex II - Chapter 17.');
            }
            const ibcReg = payload.Category?.Regulations?.IbcRegulation;
            if (!ibcReg?.Hazards) {
                errors.push('IBC Hazard Rules is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.ShipType) {
                errors.push('IBC Ship Type is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.TankType) {
                errors.push('IBC Tank Type is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.TankVents) {
                errors.push('IBC Tank Ventilation is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.TankEnvironmentalControl) {
                errors.push('IBC Environment Control is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.Gauging) {
                errors.push('IBC Gauging is required for Annex II - Chapter 17.');
            }
            if (!ibcReg?.VapourDetection) {
                errors.push('IBC Vapour Detection is required for Annex II - Chapter 17.');
            }
        }

        // Annex II - Chapter 18 (3): Product Name + Marpol Pollution Category required
        if (marpolReg === 3) {
            if (payload.Category?.Regulations?.MarpolPollutionCategory === null ||
                payload.Category?.Regulations?.MarpolPollutionCategory === undefined ||
                payload.Category?.Regulations?.MarpolPollutionCategory === 0) {
                errors.push('Marpol Pollution Category is required for Annex II - Chapter 18.');
            }
        }

        // Warning on max adjacent temperature for all Annex types
        if (marpolReg === 1 || marpolReg === 2 || marpolReg === 3) {
            const tr = payload.TemperatureRequirements;
            if (!tr?.maxAdjacentTemperature?.value && tr?.maxAdjacentTemperature?.value !== 0) {
                warnings.push('Max Adjacent Temperature is not set. Consider adding it for the selected regulation.');
            }
        }

        // Annex II (Chapter 17 and 18): Coating Resistance List
        // No client-side validation needed — the auto-merge effect in CoatingCompatibilitiesSection
        // automatically adds all active coatings with "?" resistance when entering edit mode.
        // Charterer-specific products inherit coatings read-only from their category.
    }

    // Additional validation warnings (non-blocking)
    if (payload.PhysicalData) {
        const pd = payload.PhysicalData;
        
        // Check for potentially invalid numeric values
        if (pd.LitreWeight?.Value !== null && pd.LitreWeight?.Value !== undefined) {
            if (pd.LitreWeight.Value < 0 || pd.LitreWeight.Value > 10) {
                warnings.push(`LitreWeight value ${pd.LitreWeight.Value} seems unusual (expected 0-10 kg/L).`);
            }
        }

        // Flash point sanity check
        if (pd.FlashPointRange?.MinValue?.Value !== null && pd.FlashPointRange?.MinValue?.Value !== undefined) {
            if (pd.FlashPointRange.MinValue.Value < -100 || pd.FlashPointRange.MinValue.Value > 500) {
                warnings.push(`Flash point min value ${pd.FlashPointRange.MinValue.Value} seems unusual.`);
            }
        }

        // Boiling point sanity check
        if (pd.BoilingPointRange?.MinValue?.Value !== null && pd.BoilingPointRange?.MinValue?.Value !== undefined) {
            if (pd.BoilingPointRange.MinValue.Value < -273 || pd.BoilingPointRange.MinValue.Value > 1000) {
                warnings.push(`Boiling point min value ${pd.BoilingPointRange.MinValue.Value} seems unusual.`);
            }
        }

        // pH range check
        if (pd.PHRange?.MinValue !== null && pd.PHRange?.MinValue !== undefined) {
            if (pd.PHRange.MinValue < 0 || pd.PHRange.MinValue > 14) {
                warnings.push(`pH min value ${pd.PHRange.MinValue} is outside valid range (0-14).`);
            }
        }
        if (pd.PHRange?.MaxValue !== null && pd.PHRange?.MaxValue !== undefined) {
            if (pd.PHRange.MaxValue < 0 || pd.PHRange.MaxValue > 14) {
                warnings.push(`pH max value ${pd.PHRange.MaxValue} is outside valid range (0-14).`);
            }
        }
    }

    // Log validation results for debugging
    console.log('[validateProductForUpsert] Validation result:', {
        isValid: errors.length === 0,
        errors,
        warnings,
        payload: {
            Id: payload.Id,
            TradeName: payload.TradeName,
            IsApproved: payload.IsApproved,
            PhysicalData: payload.PhysicalData,
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
